import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUp, Link as LinkIcon, Trash2, Download, Lock, Eye, Loader2, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function DocumentsManager({ clientId }) {
  const qc = useQueryClient();
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["docs", clientId],
    queryFn: async () => (await api.get(`/agent/clients/${clientId}/documents`)).data,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["docs", clientId] });

  const [link, setLink] = useState({ title: "", url: "", visibility: "client" });
  const [file, setFile] = useState(null);
  const [fileMeta, setFileMeta] = useState({ title: "", visibility: "client" });

  const addLink = useMutation({
    mutationFn: async () => (await api.post(`/agent/clients/${clientId}/documents/link`, link)).data,
    onSuccess: () => { invalidate(); setLink({ title: "", url: "", visibility: "client" }); toast.success("Link added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", fileMeta.title || file.name);
      fd.append("visibility", fileMeta.visibility);
      return (await api.post(`/agent/clients/${clientId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { invalidate(); setFile(null); setFileMeta({ title: "", visibility: "client" }); toast.success("File uploaded"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/agent/documents/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("Removed"); },
  });

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-5 rounded-2xl space-y-5">
        <div>
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><FileUp className="h-4 w-4" /> Upload a file</h3>
          <div className="space-y-3">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
            <Input value={fileMeta.title} onChange={(e) => setFileMeta({ ...fileMeta, title: e.target.value })} placeholder="Title (optional)" data-testid="doc-file-title" />
            <VisibilitySelect value={fileMeta.visibility} onChange={(v) => setFileMeta({ ...fileMeta, visibility: v })} testid="doc-file-visibility" />
            <Button onClick={() => upload.mutate()} disabled={!file || upload.isPending} className="rounded-full gap-1" data-testid="doc-upload-btn">
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Upload (max 15 MB)
            </Button>
          </div>
        </div>
        <div className="border-t border-border pt-5">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><LinkIcon className="h-4 w-4" /> Add a link</h3>
          <div className="space-y-3">
            <Input value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} placeholder="Title" data-testid="doc-link-title" />
            <Input value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://…" data-testid="doc-link-url" />
            <VisibilitySelect value={link.visibility} onChange={(v) => setLink({ ...link, visibility: v })} testid="doc-link-visibility" />
            <Button onClick={() => addLink.mutate()} disabled={!link.title || !link.url || addLink.isPending} className="rounded-full gap-1" data-testid="doc-link-btn">
              <LinkIcon className="h-4 w-4" /> Add link
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl">
        <h3 className="font-heading font-semibold mb-3">Documents & links ({docs.length})</h3>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing yet. Upload a file or add a link.</p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 p-3 rounded-xl border border-border" data-testid={`doc-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {d.visibility === "agent"
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-primary"><Lock className="h-3 w-3" /> Agent only</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] text-status-complete"><Eye className="h-3 w-3" /> Client can view</span>}
                    <span className="text-[11px] text-muted-foreground">{d.kind === "file" ? d.filename : "link"}</span>
                  </div>
                </div>
                {d.kind === "file"
                  ? <a href={`${BACKEND}/api/documents/${d.id}/download`} className="shrink-0 h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center" title="Download" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></a>
                  : <a href={d.url} target="_blank" rel="noreferrer" className="shrink-0 h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center" title="Open"><ExternalLink className="h-4 w-4" /></a>}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => del.mutate(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function VisibilitySelect({ value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Who can see this?</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="client">Client can view &amp; download</SelectItem>
          <SelectItem value="agent">Agent only (hidden from buyer)</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
